import { CalendarDays, Download, MapPin, Printer } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { api, type PublicProgramme, type PublicWebsite } from "../api.js";
import { apiErrorMessage } from "../../api/client.js";
import { Brand } from "../../design/components/Brand.js";

function displayDate(value: string | null) {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })
    .format(new Date(`${value}T00:00:00Z`));
}

function dateRange(site: PublicWebsite) {
  const start = displayDate(site.conference.starts_on);
  const end = displayDate(site.conference.ends_on);
  if (start && end) return `${start} – ${end}`;
  return start || end;
}

export function PublicConferenceSitePage() {
  const { organizationId = "", publicSlug = "" } = useParams();
  const [site, setSite] = useState<PublicWebsite | null>(null);
  const [programme, setProgramme] = useState<PublicProgramme | null>(null);
  const [error, setError] = useState("");
  const [programmeSearch, setProgrammeSearch] = useState("");
  const [programmeDay, setProgrammeDay] = useState("");
  const [programmeRoom, setProgrammeRoom] = useState("");
  const [programmeKind, setProgrammeKind] = useState("");

  const sessions = programme?.sessions ?? [];
  const programmeDays = useMemo(() => [...new Set(sessions.map((session) => session.day))], [sessions]);
  const programmeRooms = useMemo(() => [...new Set(sessions.map((session) => session.room))], [sessions]);
  const programmeKinds = useMemo(() => [...new Set(sessions.map((session) => session.kind))], [sessions]);
  const visibleSessions = useMemo(() => {
    const query = programmeSearch.trim().toLocaleLowerCase();
    return sessions.filter((session) => {
      if (programmeDay && session.day !== programmeDay) return false;
      if (programmeRoom && session.room !== programmeRoom) return false;
      if (programmeKind && session.kind !== programmeKind) return false;
      if (!query) return true;
      const searchable = [
        session.title, session.description, session.track_label, session.day, session.room,
        session.location, ...session.chairs,
        ...session.slots.flatMap((slot) => [slot.reference, slot.title, slot.abstract, ...slot.presenters]),
      ].filter(Boolean).join(" ").toLocaleLowerCase();
      return searchable.includes(query);
    });
  }, [programmeDay, programmeKind, programmeRoom, programmeSearch, sessions]);

  useEffect(() => {
    let active = true;
    setError("");
    api.publicWebsite(organizationId, publicSlug)
      .then((payload) => {
        if (!active) return;
        setSite(payload);
        void api.publicProgramme(payload.conference.id, organizationId)
          .then((result) => { if (active) setProgramme(result); })
          .catch(() => { if (active) setProgramme(null); });
      })
      .catch((reason) => { if (active) setError(apiErrorMessage(reason, "This conference website is not available.")); });
    return () => { active = false; };
  }, [organizationId, publicSlug]);

  useEffect(() => {
    if (!site) return;
    const previousTitle = document.title;
    document.title = `${site.conference.name} | Conferis`;
    return () => { document.title = previousTitle; };
  }, [site]);

  if (error) return <main className="public-site-state"><Brand href="/" /><p>Conference website</p><h1>Website unavailable</h1><span>{error}</span><a href="/">Return to Conferis</a></main>;
  if (!site) return <main className="public-site-state" aria-busy="true"><Brand href="/" /><p>Loading conference website…</p></main>;

  const dates = dateRange(site);
  const submissionEntry = site.entry_points?.submissions;
  const registrationEntry = site.entry_points?.registration;
  const programmeEntry = site.entry_points?.programme;
  return <div className={`public-conference-site public-conference-site--${site.template.toLowerCase()} public-conference-site--${site.responsive.density?.toLowerCase() || "comfortable"}${site.responsive.hero_alignment === "CENTER" ? " public-conference-site--hero-center" : ""}`}>
    <header className="public-site-header">
      <Brand href="/" />
      <nav aria-label="Conference pages">
        {site.sections.map((section) => <a href={`#${section.stable_key}`} key={section.stable_key}>{section.heading}</a>)}
      </nav>
    </header>
    <main>
      <section className="public-site-identity" aria-label="Conference details">
        <p>{site.conference.short_name || "Conference"}</p>
        <h1>{site.conference.name}</h1>
        <div>
          {dates ? <span><CalendarDays size={17} aria-hidden="true" />{dates}</span> : null}
          {site.conference.venue ? <span><MapPin size={17} aria-hidden="true" />{site.conference.venue}</span> : null}
        </div>
        {registrationEntry?.available || submissionEntry?.available || programmeEntry?.available ? <div className="public-site-primary-actions">
          {registrationEntry?.available ? <a className="public-site-cta" href={registrationEntry.path}>Register to attend</a> : null}
          {submissionEntry?.available ? <a className="public-site-secondary-action" href={submissionEntry.path}>Submit your work</a> : null}
          {programmeEntry?.available ? <a className="public-site-secondary-action" href="#programme">View programme</a> : null}
        </div> : null}
      </section>
      <div className="public-site-sections">
        {site.sections.map((section, index) => <section id={section.stable_key} className={index === 0 ? "is-hero" : ""} key={section.stable_key}>
          <p className="public-site-eyebrow">{section.type.replaceAll("_", " ")}</p>
          <h2>{section.heading}</h2>
          {section.body ? <p className="public-site-copy">{section.body}</p> : null}
          {section.items?.length ? <ul>{section.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
          {section.cta_label && section.cta_url ? <a className="public-site-cta" href={section.cta_url}>{section.cta_label}</a> : null}
          {!section.cta_label && section.type === "CALL_FOR_PAPERS" && submissionEntry?.available ? <a className="public-site-cta" href={submissionEntry.path}>Start a submission</a> : null}
          {!section.cta_label && section.type === "REGISTRATION" && registrationEntry?.available ? <a className="public-site-cta" href={registrationEntry.path}>Register to attend</a> : null}
          {!section.cta_label && section.type === "PROGRAMME" && programmeEntry?.available ? <a className="public-site-cta" href="#programme">Explore the programme</a> : null}
        </section>)}
      </div>
      {programme?.sessions.length ? <section id="programme" className="public-programme-section">
        <header><p className="public-site-eyebrow">Programme</p><h2>Conference programme</h2><span>The current published schedule. Times are shown in your device time zone.</span><div className="programme-download-actions"><a href={`/api/v1/public/conferences/${site.conference.id}/programme/calendar/?organization_id=${organizationId}`} download="programme.ics"><Download size={16} aria-hidden="true" />Download calendar</a><a href={`/api/v1/public/conferences/${site.conference.id}/programme.csv?organization_id=${organizationId}`} download="programme.csv"><Download size={16} aria-hidden="true" />Download CSV</a><button type="button" onClick={() => window.print()}><Printer size={16} aria-hidden="true" />Print programme</button></div></header>
        <form className="public-programme-filters" role="search" aria-label="Filter conference programme" onSubmit={(event) => event.preventDefault()}>
          <label>Search programme<input type="search" value={programmeSearch} onChange={(event) => setProgrammeSearch(event.target.value)} placeholder="Sessions, papers, presenters…" /></label>
          <label>Day<select value={programmeDay} onChange={(event) => setProgrammeDay(event.target.value)}><option value="">All days</option>{programmeDays.map((day) => <option value={day} key={day}>{day}</option>)}</select></label>
          <label>Room<select value={programmeRoom} onChange={(event) => setProgrammeRoom(event.target.value)}><option value="">All rooms</option>{programmeRooms.map((room) => <option value={room} key={room}>{room}</option>)}</select></label>
          <label>Session type<select value={programmeKind} onChange={(event) => setProgrammeKind(event.target.value)}><option value="">All types</option>{programmeKinds.map((kind) => <option value={kind} key={kind}>{kind.replaceAll("_", " ").toLocaleLowerCase()}</option>)}</select></label>
          <p aria-live="polite">{visibleSessions.length} of {sessions.length} sessions</p>
        </form>
        {visibleSessions.length ? <div>{visibleSessions.map((session, index) => <article key={`${session.starts_at}-${index}`}>
          <time>{new Date(session.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>
          <div><span>{session.day} · {session.date}</span><div className="public-programme-tags"><b>{session.kind.replaceAll("_", " ")}</b>{session.track_label ? <b>{session.track_label}</b> : null}</div><h3>{session.title}</h3><p>{session.room}{session.location ? ` · ${session.location}` : ""}</p>{session.description ? <p className="public-programme-description">{session.description}</p> : null}{session.chairs.length ? <p className="public-programme-chairs">Chaired by {session.chairs.join(" · ")}</p> : null}{session.slots.length ? <ul>{session.slots.map((slot, slotIndex) => <li key={`${slot.starts_at}-${slotIndex}`}><span>{new Date(slot.starts_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span><strong>{slot.title}</strong>{slot.reference ? <em>{slot.reference}</em> : null}{slot.presenters.length ? <small>{slot.presenters.join(" · ")}</small> : null}{slot.abstract ? <p>{slot.abstract}</p> : null}</li>)}</ul> : null}</div>
        </article>)}</div> : <p className="public-programme-empty">No sessions match these filters.</p>}
      </section> : null}
    </main>
    <footer><Brand href="/" /><p>Conference management, simplified.</p><span>Published with Conferis · Website version {site.version}</span></footer>
  </div>;
}
